import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, X, Trash2, Lock, Share2, Phone, Mail, MessageCircle, Save, Maximize2, Minimize2, Link as LinkIcon, StickyNote } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
    fetchList, updateList, deleteList,
    addItem, patchItem, deleteItem,
    addContact, patchContact, deleteContact,
} from '../lib/lists';

export default function ListDetail() {
    const { listId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [list, setList] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([
            fetchList(listId),
            api.get('/family/members').then((r) => r.data.members || []),
        ]).then(([l, m]) => {
            setList(l);
            setMembers(m);
        }).catch((e) => {
            setError(e?.response?.data?.detail || 'No pudimos cargar la lista');
        }).finally(() => setLoading(false));
    }, [listId]);

    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar esta lista? Esta acción no se puede deshacer.')) return;
        try {
            await deleteList(listId);
            navigate('/listas', { replace: true });
        } catch (e) {
            alert(e?.response?.data?.detail || 'No pudimos eliminar la lista');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-surface flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
            </div>
        );
    }
    if (error || !list) {
        return (
            <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6">
                <p className="text-sm text-red-600 mb-4">{error || 'Lista no disponible'}</p>
                <button onClick={() => navigate('/listas')} className="text-brand font-bold">Volver a Listas</button>
            </div>
        );
    }

    const sharedMembers = (list.shared_with || [])
        .map((id) => members.find((m) => m.id === id))
        .filter(Boolean);
    const isCreator = list.created_by === user?.id;

    return (
        <div className="min-h-screen bg-surface app-shell pb-32">
            <div className="px-6 pt-4">
                <div className="flex items-center justify-between">
                    <button onClick={() => navigate(-1)} className="w-10 h-10 -ml-2 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="list-detail-back">
                        <ArrowLeft className="w-5 h-5 text-brand-navy" />
                    </button>
                    <h1 className="text-base font-bold text-brand-navy">Lista</h1>
                    <button onClick={handleDelete} className="w-10 h-10 rounded-full hover:bg-red-50 flex items-center justify-center" data-testid="list-detail-delete">
                        <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                </div>

                <div className="mt-4 flex items-center gap-3">
                    <span className="text-4xl">{list.icon}</span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-brand-navy truncate" data-testid="list-detail-name">{list.name}</h1>
                            {list.is_private && <Lock className="w-4 h-4 text-gray-400" />}
                        </div>
                        <div className="text-xs text-gray-500">{formatDate(list.updated_at)}</div>
                    </div>
                </div>

                {list.type === 'elementos' && <ElementosBody list={list} setList={setList} />}
                {list.type === 'nota' && <NotaBody list={list} setList={setList} canEdit />}
                {list.type === 'contactos' && <ContactosBody list={list} setList={setList} isPrivate={list.is_private} />}

                <SharedWithBlock list={list} sharedMembers={sharedMembers} members={members} setList={setList} isCreator={isCreator} />
            </div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const hours = Math.floor(diffMs / 3600000);
    if (hours < 1) return 'Hace minutos';
    if (hours < 24) return 'Hoy';
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Ayer';
    if (days < 30) return `Hace ${days} días`;
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ---------- ELEMENTOS ----------
function ElementosBody({ list, setList }) {
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const items = list.items || [];
    const total = list.progress?.total || items.length;
    const completed = list.progress?.completed || items.filter((i) => i.completed).length;
    const percent = list.progress?.percent || (total ? Math.round((completed / total) * 100) : 0);

    const onAdd = async () => {
        const t = draft.trim();
        if (!t) return;
        setBusy(true);
        try {
            const updated = await addItem(list.id, t);
            setList(updated);
            setDraft('');
        } finally {
            setBusy(false);
        }
    };

    const toggle = async (item) => {
        const updated = await patchItem(list.id, item.id, { completed: !item.completed });
        setList(updated);
    };

    const remove = async (item) => {
        const updated = await deleteItem(list.id, item.id);
        setList(updated);
    };

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>{completed} de {total} completados</span>
                <span className="font-semibold">{percent}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand transition-all" style={{ width: `${percent}%` }} data-testid="elementos-progress" />
            </div>

            <div className="mt-5 flex items-center gap-2">
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && onAdd()}
                    placeholder="Agregar elemento…"
                    className="flex-1 h-12 bg-white border border-gray-200 rounded-2xl px-4 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20"
                    data-testid="elementos-input"
                />
                <button onClick={onAdd} disabled={busy} className="w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center shadow-brand-cta disabled:opacity-50" data-testid="elementos-add">
                    <Plus className="w-5 h-5" />
                </button>
            </div>

            <div className="mt-4 space-y-2" data-testid="elementos-list">
                {items.map((it) => (
                    <div key={it.id} className="flex items-center gap-3 bg-white rounded-2xl shadow-card px-3 py-2.5">
                        <button
                            onClick={() => toggle(it)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition ${it.completed ? 'bg-brand border-brand' : 'border-gray-300 bg-white'}`}
                            data-testid={`elemento-toggle-${it.id}`}
                        >
                            {it.completed && <span className="block w-2 h-2 bg-white rounded-full" />}
                        </button>
                        <span className={`flex-1 text-sm ${it.completed ? 'line-through text-gray-400' : 'text-brand-navy'}`}>{it.text}</span>
                        <button onClick={() => remove(it)} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center" data-testid={`elemento-remove-${it.id}`}>
                            <X className="w-4 h-4 text-gray-300 hover:text-red-500" />
                        </button>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="text-center text-xs text-gray-400 py-6">Sin elementos aún. Agrega el primero arriba ☝️</div>
                )}
            </div>
        </div>
    );
}

// ---------- NOTA ----------
function NotaBody({ list, setList }) {
    const [editing, setEditing] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [content, setContent] = useState(list.note_content || '');
    const [extra, setExtra] = useState(list.note_extra || '');
    const [linksText, setLinksText] = useState((list.note_links || []).join('\n'));
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const links = linksText.split('\n').map((s) => s.trim()).filter(Boolean);
            const updated = await updateList(list.id, {
                note_content: content,
                note_extra: extra,
                note_links: links,
            });
            setList(updated);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className={`mt-6 ${expanded ? 'fixed inset-0 z-50 bg-white p-6 overflow-y-auto' : ''}`}>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold text-brand-navy">Editar nota</h2>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setExpanded(!expanded)} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="nota-expand-toggle">
                            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </button>
                        <button onClick={save} disabled={saving} className="px-4 h-9 rounded-full bg-brand text-white text-sm font-bold disabled:opacity-50" data-testid="nota-save">
                            <Save className="w-4 h-4 inline mr-1" />Guardar
                        </button>
                    </div>
                </div>
                <label className="text-xs font-bold text-gray-500 uppercase">Contenido</label>
                <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={expanded ? 20 : 10}
                    className="w-full mt-1 mb-3 bg-white border border-gray-200 rounded-2xl p-3 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20"
                    placeholder="Escribe… Markdown soportado (- listas, 1. numeradas)"
                    data-testid="nota-content-edit"
                />
                <label className="text-xs font-bold text-gray-500 uppercase">Nota adicional</label>
                <textarea
                    value={extra}
                    onChange={(e) => setExtra(e.target.value)}
                    rows={3}
                    className="w-full mt-1 mb-3 bg-white border border-gray-200 rounded-2xl p-3 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20"
                    placeholder="Notas o contexto adicional"
                    data-testid="nota-extra-edit"
                />
                <label className="text-xs font-bold text-gray-500 uppercase">Enlaces (uno por línea)</label>
                <textarea
                    value={linksText}
                    onChange={(e) => setLinksText(e.target.value)}
                    rows={3}
                    className="w-full mt-1 bg-white border border-gray-200 rounded-2xl p-3 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20"
                    placeholder="https://…"
                    data-testid="nota-links-edit"
                />
            </div>
        );
    }

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-brand-navy uppercase tracking-wide">Contenido</h2>
                <button onClick={() => setEditing(true)} className="px-3 h-8 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center gap-1" data-testid="nota-edit">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
            </div>
            <div className="bg-white rounded-2xl shadow-card p-4" data-testid="nota-content-view">
                {list.note_content
                    ? <MarkdownView text={list.note_content} />
                    : <p className="text-xs text-gray-400 italic">Aún sin contenido. Toca "Editar" para empezar.</p>
                }
            </div>

            {list.note_extra && (
                <div className="mt-4">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Contenido complementario</h3>
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex gap-2">
                        <StickyNote className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-900 leading-relaxed">{list.note_extra}</p>
                    </div>
                </div>
            )}

            {(list.note_links || []).length > 0 && (
                <div className="mt-4">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Enlaces</h3>
                    <div className="space-y-2">
                        {list.note_links.map((l, i) => (
                            <a
                                key={i}
                                href={l}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-white rounded-2xl shadow-card p-3 text-sm text-brand break-all hover:bg-brand/5"
                                data-testid={`nota-link-${i}`}
                            >
                                <LinkIcon className="w-4 h-4 flex-shrink-0" />
                                {l}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function MarkdownView({ text }) {
    // Lightweight renderer: lines starting with `- ` -> bullets; `1.`, `2.` -> ordered; preserves blank lines and headings
    const lines = text.split('\n');
    const blocks = [];
    let current = null;

    const flush = () => {
        if (current) blocks.push(current);
        current = null;
    };

    lines.forEach((raw, idx) => {
        const line = raw.replace(/\r$/, '');
        if (/^\s*-\s+/.test(line)) {
            if (!current || current.type !== 'ul') { flush(); current = { type: 'ul', items: [] }; }
            current.items.push(line.replace(/^\s*-\s+/, ''));
        } else if (/^\s*\d+\.\s+/.test(line)) {
            if (!current || current.type !== 'ol') { flush(); current = { type: 'ol', items: [] }; }
            current.items.push(line.replace(/^\s*\d+\.\s+/, ''));
        } else if (line.trim() === '') {
            flush();
            blocks.push({ type: 'br', key: `br-${idx}` });
        } else {
            if (!current || current.type !== 'p') { flush(); current = { type: 'p', text: '' }; }
            current.text += (current.text ? '\n' : '') + line;
        }
    });
    flush();

    return (
        <div className="text-sm text-gray-800 leading-relaxed space-y-2">
            {blocks.map((b, i) => {
                if (b.type === 'ul') return <ul key={i} className="list-disc pl-5 space-y-1">{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
                if (b.type === 'ol') return <ol key={i} className="list-decimal pl-5 space-y-1">{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>;
                if (b.type === 'br') return <div key={b.key} className="h-1" />;
                return <p key={i} className="whitespace-pre-wrap">{renderInline(b.text)}</p>;
            })}
        </div>
    );
}

function renderInline(t) {
    // bold **x** and italic *x*
    const parts = [];
    let rest = t;
    let key = 0;
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*)/;
    while (rest) {
        const m = rest.match(re);
        if (!m) { parts.push(rest); break; }
        const i = m.index;
        if (i > 0) parts.push(rest.slice(0, i));
        const token = m[0];
        if (token.startsWith('**')) parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
        else parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
        rest = rest.slice(i + token.length);
    }
    return parts;
}

// ---------- CONTACTOS ----------
function ContactosBody({ list, setList, isPrivate }) {
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState({ name: '', role: '', phone: '', email: '' });
    const contacts = list.contacts || [];

    const onAdd = async () => {
        if (!draft.name.trim()) return;
        const updated = await addContact(list.id, {
            name: draft.name.trim(),
            role: draft.role || null,
            phone: draft.phone || null,
            email: draft.email || null,
        });
        setList(updated);
        setDraft({ name: '', role: '', phone: '', email: '' });
        setAdding(false);
    };

    const remove = async (c) => {
        if (!window.confirm(`¿Eliminar a ${c.name}?`)) return;
        const updated = await deleteContact(list.id, c.id);
        setList(updated);
    };

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
                <span>{contacts.length} contacto{contacts.length !== 1 && 's'}</span>
                {isPrivate && <span className="flex items-center gap-1 text-amber-700"><Lock className="w-3 h-3" /> Privada</span>}
            </div>
            <div className="space-y-3" data-testid="contactos-list">
                {contacts.map((c) => (
                    <div key={c.id} className="bg-white rounded-2xl shadow-card p-4" data-testid={`contacto-card-${c.id}`}>
                        <div className="flex items-center gap-3 mb-3">
                            <span className="w-11 h-11 rounded-full bg-brand/10 text-brand text-sm font-bold flex items-center justify-center">
                                {(c.name || '?').charAt(0).toUpperCase()}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold text-brand-navy truncate">{c.name}</div>
                                {c.role && <div className="text-[11px] text-gray-500 truncate">{c.role}</div>}
                            </div>
                            <button onClick={() => remove(c)} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center" data-testid={`contacto-remove-${c.id}`}>
                                <Trash2 className="w-4 h-4 text-gray-300 hover:text-red-500" />
                            </button>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {c.phone && (
                                <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold" data-testid={`contacto-call-${c.id}`}>
                                    <Phone className="w-3.5 h-3.5" /> Llamar
                                </a>
                            )}
                            {c.phone && (
                                <a href={`sms:${c.phone}`} className="flex items-center gap-1.5 h-9 px-3 rounded-full bg-blue-50 text-blue-700 text-xs font-bold" data-testid={`contacto-sms-${c.id}`}>
                                    <MessageCircle className="w-3.5 h-3.5" /> Mensaje
                                </a>
                            )}
                            {c.email && (
                                <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 h-9 px-3 rounded-full bg-purple-50 text-purple-700 text-xs font-bold" data-testid={`contacto-email-${c.id}`}>
                                    <Mail className="w-3.5 h-3.5" /> Email
                                </a>
                            )}
                        </div>
                        {(c.phone || c.email) && (
                            <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 space-y-1">
                                {c.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{c.phone}</div>}
                                {c.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{c.email}</div>}
                            </div>
                        )}
                    </div>
                ))}
                {contacts.length === 0 && !adding && (
                    <div className="text-center text-xs text-gray-400 py-6">Sin contactos aún. Agrega el primero abajo 👇</div>
                )}
            </div>

            {adding ? (
                <div className="mt-4 bg-white border border-dashed border-brand/40 rounded-2xl p-4 space-y-2">
                    <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Nombre" className="w-full h-11 bg-gray-50 border border-gray-200 rounded-xl px-3 text-sm" data-testid="contacto-draft-name" autoFocus />
                    <input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="Rol o relación" className="w-full h-11 bg-gray-50 border border-gray-200 rounded-xl px-3 text-sm" data-testid="contacto-draft-role" />
                    <input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="Teléfono" className="w-full h-11 bg-gray-50 border border-gray-200 rounded-xl px-3 text-sm" data-testid="contacto-draft-phone" />
                    <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="Email" className="w-full h-11 bg-gray-50 border border-gray-200 rounded-xl px-3 text-sm" data-testid="contacto-draft-email" />
                    <div className="flex gap-2">
                        <button onClick={() => setAdding(false)} className="flex-1 h-11 rounded-full bg-gray-100 text-gray-600 font-bold text-sm" data-testid="contacto-cancel">Cancelar</button>
                        <button onClick={onAdd} className="flex-1 h-11 rounded-full bg-brand text-white font-bold text-sm" data-testid="contacto-save">Guardar</button>
                    </div>
                </div>
            ) : (
                <button onClick={() => setAdding(true)} className="mt-4 w-full h-12 rounded-full bg-brand/10 text-brand font-bold text-sm flex items-center justify-center gap-2" data-testid="contactos-add-fab">
                    <Plus className="w-4 h-4" /> Agregar contacto
                </button>
            )}
        </div>
    );
}

// ---------- COMPARTIDA CON ----------
function SharedWithBlock({ list, sharedMembers, members, setList, isCreator }) {
    const [editing, setEditing] = useState(false);
    const [selected, setSelected] = useState(list.shared_with || []);
    const [isPrivate, setIsPrivate] = useState(list.is_private);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await updateList(list.id, { is_private: isPrivate, shared_with: selected });
            setList(updated);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

    if (editing) {
        return (
            <div className="mt-6 bg-white rounded-2xl shadow-card p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-brand-navy">Configurar acceso</h3>
                    <button onClick={save} disabled={saving} className="px-3 h-8 rounded-full bg-brand text-white text-xs font-bold" data-testid="share-save">Guardar</button>
                </div>
                <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-2xl p-3 mb-3">
                    <div className="flex items-center gap-3">
                        <Lock className="w-5 h-5 text-orange-500" />
                        <div>
                            <div className="text-sm font-bold text-brand-navy">Lista privada</div>
                            <div className="text-xs text-gray-500">Solo el creador y los miembros invitados verán la lista</div>
                        </div>
                    </div>
                    <button onClick={() => setIsPrivate(!isPrivate)} className={`relative w-11 h-6 rounded-full transition ${isPrivate ? 'bg-brand' : 'bg-gray-300'}`} data-testid="share-private-toggle">
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition ${isPrivate ? 'left-5' : 'left-0.5'}`} />
                    </button>
                </div>
                {isPrivate && (
                    <div>
                        <div className="text-xs font-bold text-gray-500 uppercase mb-2">Miembros con acceso</div>
                        <div className="flex flex-wrap gap-2">
                            {members.map((m) => (
                                <button
                                    key={m.id}
                                    onClick={() => toggle(m.id)}
                                    className={`flex items-center gap-2 h-9 px-3 rounded-full text-xs font-bold border transition ${selected.includes(m.id) ? 'bg-brand/10 border-brand text-brand' : 'bg-white border-gray-200 text-gray-500'}`}
                                    data-testid={`share-member-${m.id}`}
                                >
                                    <span className="w-6 h-6 rounded-full bg-gray-100 text-[11px] flex items-center justify-center">{(m.name || '?').charAt(0)}</span>
                                    {(m.name || '').split(' ')[0]}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="mt-6 bg-white rounded-2xl shadow-card p-4" data-testid="shared-block">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-brand" />
                    <h3 className="text-sm font-bold text-brand-navy">{list.is_private ? 'Compartida con' : 'Visible para toda la familia'}</h3>
                </div>
                {isCreator && (
                    <button onClick={() => setEditing(true)} className="text-xs font-bold text-brand" data-testid="share-edit">Editar</button>
                )}
            </div>
            {list.is_private ? (
                sharedMembers.length === 0 ? (
                    <p className="text-xs text-gray-400">Solo tú tienes acceso.</p>
                ) : (
                    <div className="flex flex-wrap gap-2 mt-2">
                        {sharedMembers.map((m) => (
                            <span key={m.id} className="flex items-center gap-2 bg-brand/5 rounded-full pl-1 pr-3 h-8 text-xs font-bold text-brand-navy" data-testid={`shared-with-${m.id}`}>
                                <span className="w-6 h-6 rounded-full bg-brand/10 text-brand text-[11px] flex items-center justify-center">{(m.name || '?').charAt(0)}</span>
                                {(m.name || '').split(' ')[0]}
                            </span>
                        ))}
                    </div>
                )
            ) : (
                <p className="text-xs text-gray-500">Cualquier miembro de la familia puede ver y editar.</p>
            )}
        </div>
    );
}
